import { CopyButton } from "@gadgetinc/widgets";
import { useStyletron } from "baseui";
import type { Language } from "prism-react-renderer";
import Highlight, { defaultProps } from "prism-react-renderer";
import React, { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { EsbuildTranspiler } from "shared-utils/src/ts-converter/EsbuildTranspiler";
import { useCodeHighlightTheme } from "web/src/components/markdown/CodeHighlight";
import { DocsContext } from "../chrome/DocsContext";
import { renameTsToJs } from "./InlineTsFilePath";
import { CodeBlockTitleBar, CodeBlockTitleBarSpacer } from "web/src/components/markdown/CodeBlock";
import { LanguageIndicator, languageLabel } from "web/src/components/markdown/LanguageIndicator";
import { CodeBlockContainer, CodeBlockFilename, CodeBlockTitle, handleCopyWithToast } from "./CodeBlock";

type CodeDiffProps = {
  before: string;
  after: string;
  title?: ReactNode;
  filename?: string;
  language?: string;
  mode?: "split" | "unified";
  startOldLine?: number;
  startNewLine?: number;
  showCopyButton?: boolean;
};

type DiffLine =
  | { type: "context"; content: string }
  | { type: "added"; content: string }
  | { type: "removed"; content: string }
  | { type: "changed"; before: string; after: string };

type DiffTone = "context" | "added" | "removed" | "changedBefore" | "changedAfter";
type DiffRenderLine = {
  key: string;
  oldLine: number | null;
  newLine: number | null;
  marker: string;
  content: string;
  tone: DiffTone;
};

const padLineNumber = (line: number | null) => (line === null ? "" : String(line));

const normalizeLanguage = (language: string | undefined): Language => {
  if (!language) return "tsx";
  if (language === "javascript" || language === "react" || language === "preact") return "jsx";
  if (language === "typescript") return "tsx";
  return language as Language;
};

const isTypeScriptLanguage = (language: string | undefined) => {
  const lower = language?.toLowerCase();
  return lower === "typescript" || lower === "ts" || lower === "tsx";
};

const normalizeMultilineIndent = (text: string) => {
  const lines = text.replaceAll("\r\n", "\n").split("\n");
  if (lines.length < 2) return text;

  const trailingLines = lines.slice(1);
  const nonEmptyTrailingLines = trailingLines.filter((line) => line.trim().length > 0);
  if (nonEmptyTrailingLines.length === 0) return text;

  const trailingMinIndent = nonEmptyTrailingLines.reduce((minIndent, line) => {
    const indent = (line.match(/^[ \t]*/) ?? [""])[0].length;
    return Math.min(minIndent, indent);
  }, Number.POSITIVE_INFINITY);

  if (!Number.isFinite(trailingMinIndent) || trailingMinIndent <= 0) return text;

  const firstLineIndent = ((lines[0] ?? "").match(/^[ \t]*/) ?? [""])[0].length;
  if (firstLineIndent >= trailingMinIndent) return text;

  const normalizedLines = lines.map((line, index) => {
    if (index === 0) return line;
    if (line.trim().length === 0) return "";
    return line.slice(trailingMinIndent);
  });

  return normalizedLines.join("\n");
};

type RawDiffOp = { type: "context" | "removed" | "added"; content: string };
type TranspiledDiff = { before: string; after: string } | null;

const transpileOptions = {
  addActionTypeComments: true,
  addRouteTypeComment: true,
};

const buildDiffLinesFromBeforeAfter = (before: string, after: string): DiffLine[] => {
  const beforeLines = before.split("\n");
  const afterLines = after.split("\n");
  const beforeLength = beforeLines.length;
  const afterLength = afterLines.length;

  const lcs: number[][] = Array.from({ length: beforeLength + 1 }, () => Array(afterLength + 1).fill(0));

  for (let i = beforeLength - 1; i >= 0; i--) {
    for (let j = afterLength - 1; j >= 0; j--) {
      if (beforeLines[i] === afterLines[j]) {
        lcs[i][j] = lcs[i + 1][j + 1] + 1;
      } else {
        lcs[i][j] = Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }
  }

  const ops: RawDiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < beforeLength && j < afterLength) {
    if (beforeLines[i] === afterLines[j]) {
      ops.push({ type: "context", content: beforeLines[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ type: "removed", content: beforeLines[i] });
      i++;
    } else {
      ops.push({ type: "added", content: afterLines[j] });
      j++;
    }
  }

  while (i < beforeLength) {
    ops.push({ type: "removed", content: beforeLines[i] });
    i++;
  }

  while (j < afterLength) {
    ops.push({ type: "added", content: afterLines[j] });
    j++;
  }

  const diffLines: DiffLine[] = [];
  let pendingRemovals: string[] = [];
  let pendingAdditions: string[] = [];

  const flushPending = () => {
    const overlapCount = Math.min(pendingRemovals.length, pendingAdditions.length);
    for (let idx = 0; idx < overlapCount; idx++) {
      diffLines.push({
        type: "changed",
        before: pendingRemovals[idx],
        after: pendingAdditions[idx],
      });
    }
    for (let idx = overlapCount; idx < pendingRemovals.length; idx++) {
      diffLines.push({ type: "removed", content: pendingRemovals[idx] });
    }
    for (let idx = overlapCount; idx < pendingAdditions.length; idx++) {
      diffLines.push({ type: "added", content: pendingAdditions[idx] });
    }
    pendingRemovals = [];
    pendingAdditions = [];
  };

  for (const op of ops) {
    if (op.type === "context") {
      flushPending();
      diffLines.push({ type: "context", content: op.content });
    } else if (op.type === "removed") {
      pendingRemovals.push(op.content);
    } else {
      pendingAdditions.push(op.content);
    }
  }
  flushPending();

  return diffLines;
};

const buildRenderLines = (
  lines: DiffLine[],
  startOldLine: number,
  startNewLine: number,
  mode: CodeDiffProps["mode"] = "split"
): DiffRenderLine[] => {
  let oldLine = startOldLine;
  let newLine = startNewLine;
  const renderLines: DiffRenderLine[] = [];

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index]!;

    if (line.type === "context") {
      renderLines.push({
        key: `line-${index}`,
        oldLine,
        newLine,
        marker: " ",
        content: line.content,
        tone: "context",
      });
      oldLine++;
      newLine++;
      continue;
    }

    if (line.type === "added") {
      renderLines.push({
        key: `line-${index}`,
        oldLine: null,
        newLine,
        marker: "+",
        content: line.content,
        tone: "added",
      });
      newLine++;
      continue;
    }

    if (line.type === "removed") {
      renderLines.push({
        key: `line-${index}`,
        oldLine,
        newLine: null,
        marker: "-",
        content: line.content,
        tone: "removed",
      });
      oldLine++;
      continue;
    }

    if (mode === "unified") {
      const changedBlock: Extract<DiffLine, { type: "changed" }>[] = [line];
      while (index + 1 < lines.length && lines[index + 1]?.type === "changed") {
        changedBlock.push(lines[index + 1] as Extract<DiffLine, { type: "changed" }>);
        index++;
      }

      for (const [blockIndex, changedLine] of changedBlock.entries()) {
        renderLines.push({
          key: `line-${index - changedBlock.length + blockIndex + 1}-before`,
          oldLine,
          newLine: null,
          marker: "-",
          content: changedLine.before,
          tone: "removed",
        });
        oldLine++;
      }

      for (const [blockIndex, changedLine] of changedBlock.entries()) {
        renderLines.push({
          key: `line-${index - changedBlock.length + blockIndex + 1}-after`,
          oldLine: null,
          newLine,
          marker: "+",
          content: changedLine.after,
          tone: "added",
        });
        newLine++;
      }
    } else {
      renderLines.push({
        key: `line-${index}-before`,
        oldLine,
        newLine: null,
        marker: "~",
        content: line.before,
        tone: "changedBefore",
      });
      renderLines.push({
        key: `line-${index}-after`,
        oldLine: null,
        newLine,
        marker: "~",
        content: line.after,
        tone: "changedAfter",
      });
    }
    oldLine++;
    newLine++;
  }

  return renderLines;
};

export const CodeDiff = (props: CodeDiffProps) => {
  const [css, $theme] = useStyletron();
  const { showTsLanguage, initializedTranspiler } = useContext(DocsContext);
  const { theme, labelColor } = useCodeHighlightTheme("dark");
  const autoBefore = useMemo(() => normalizeMultilineIndent(props.before), [props.before]);
  const autoAfter = useMemo(() => normalizeMultilineIndent(props.after), [props.after]);
  const shouldTranspile = initializedTranspiler && !showTsLanguage && isTypeScriptLanguage(props.language);
  const [transpiledDiff, setTranspiledDiff] = useState<TranspiledDiff>(null);
  const rowStyleByTone = useMemo<Record<DiffTone, { backgroundColor: string; markerColor: string }>>(
    () => ({
      context: {
        backgroundColor: "transparent",
        markerColor: $theme.colors.primary300,
      },
      added: {
        backgroundColor: `color-mix(in srgb, ${$theme.colors.positive500} 18%, transparent)`,
        markerColor: $theme.colors.positive500,
      },
      removed: {
        backgroundColor: `color-mix(in srgb, ${$theme.colors.negative500} 18%, transparent)`,
        markerColor: $theme.colors.negative500,
      },
      changedBefore: {
        backgroundColor: `color-mix(in srgb, ${$theme.colors.warning500} 18%, transparent)`,
        markerColor: $theme.colors.warning500,
      },
      changedAfter: {
        backgroundColor: `color-mix(in srgb, ${$theme.colors.warning500} 28%, transparent)`,
        markerColor: $theme.colors.warning500,
      },
    }),
    [$theme.colors.negative500, $theme.colors.positive500, $theme.colors.primary300, $theme.colors.warning500]
  );

  useEffect(() => {
    if (!shouldTranspile) {
      setTranspiledDiff(null);
      return;
    }

    let didCancel = false;
    void (async () => {
      const transpiler = new EsbuildTranspiler();
      const [beforeResult, afterResult] = await Promise.all([
        transpiler.transpile(autoBefore, true, transpileOptions),
        transpiler.transpile(autoAfter, true, transpileOptions),
      ]);

      if (!didCancel) {
        setTranspiledDiff({ before: beforeResult, after: afterResult });
      }
    })();

    return () => {
      didCancel = true;
    };
  }, [autoAfter, autoBefore, shouldTranspile]);

  const effectiveBefore = shouldTranspile ? (transpiledDiff?.before ?? autoBefore) : autoBefore;
  const effectiveAfter = shouldTranspile ? (transpiledDiff?.after ?? autoAfter) : autoAfter;
  const diffLines = useMemo(() => buildDiffLinesFromBeforeAfter(effectiveBefore, effectiveAfter), [effectiveBefore, effectiveAfter]);
  const updatedFile = effectiveAfter;
  const renderLines = useMemo(
    () => buildRenderLines(diffLines, props.startOldLine ?? 1, props.startNewLine ?? 1, props.mode),
    [diffLines, props.mode, props.startOldLine, props.startNewLine]
  );
  const language = shouldTranspile ? "javascript" : props.language?.toLowerCase();
  const filename = props.filename ? renameTsToJs(props.filename, showTsLanguage) : undefined;
  const highlightLanguage = normalizeLanguage(language);
  const showCopyButton = props.showCopyButton ?? false;

  const copyCode = useCallback(() => {
    void handleCopyWithToast(updatedFile);
  }, [updatedFile]);
  const renderHighlightedLineContent = useCallback(
    (lineContent: string) => {
      if (typeof window == "undefined") return lineContent;

      return (
        <Highlight {...defaultProps} code={lineContent} language={highlightLanguage} theme={theme}>
          {({ tokens, getTokenProps }) => (tokens[0] ?? []).map((token, key) => <span key={key} {...getTokenProps({ token, key })} />)}
        </Highlight>
      );
    },
    [highlightLanguage, theme]
  );

  return (
    <CodeBlockContainer rawCode={updatedFile} language={language}>
      <CodeBlockTitleBar
        $style={
          showCopyButton
            ? undefined
            : {
                paddingTop: $theme.sizing.scale400,
                paddingBottom: $theme.sizing.scale400,
                paddingLeft: $theme.sizing.scale800,
                paddingRight: $theme.sizing.scale500,
              }
        }
      >
        {props.title && <CodeBlockTitle>{props.title}</CodeBlockTitle>}
        {filename && <CodeBlockFilename $theme={$theme}>{filename}</CodeBlockFilename>}
        <CodeBlockTitleBarSpacer />
        {language && <LanguageIndicator color={labelColor}>{languageLabel(language)}</LanguageIndicator>}
        {showCopyButton && <CopyButton onClick={copyCode} />}
      </CodeBlockTitleBar>
      <div
        className={css({
          overflowX: "auto",
          paddingLeft: $theme.sizing.scale400,
          paddingRight: $theme.sizing.scale400,
          paddingBottom: $theme.sizing.scale400,
        })}
      >
        {renderLines.map((line) => {
          const rowStyle = rowStyleByTone[line.tone];
          return (
            <div
              key={line.key}
              data-codediff-tone={line.tone}
              className={css({
                display: "grid",
                gridTemplateColumns: "3ch 3ch 1ch minmax(0, 1fr)",
                alignItems: "start",
                columnGap: "0.5em",
                backgroundColor: rowStyle.backgroundColor,
              })}
            >
              <span
                className={css({
                  ...$theme.typography.MonoParagraphSmall,
                  color: labelColor,
                  opacity: 0.5,
                  textAlign: "right",
                  userSelect: "none",
                  fontVariantNumeric: "tabular-nums",
                })}
              >
                {padLineNumber(line.oldLine)}
              </span>
              <span
                className={css({
                  ...$theme.typography.MonoParagraphSmall,
                  color: labelColor,
                  opacity: 0.5,
                  textAlign: "right",
                  userSelect: "none",
                  fontVariantNumeric: "tabular-nums",
                })}
              >
                {padLineNumber(line.newLine)}
              </span>
              <span
                data-codediff-marker={line.marker}
                className={css({
                  ...$theme.typography.MonoParagraphSmall,
                  color: rowStyle.markerColor,
                  userSelect: "none",
                  textAlign: "center",
                  opacity: 0.9,
                })}
              >
                {line.marker}
              </span>
              <span
                className={css({
                  ...$theme.typography.MonoParagraphSmall,
                  color: theme.plain.color ?? "rgba(255, 255, 255, 0.92)",
                  whiteSpace: "pre",
                })}
              >
                {renderHighlightedLineContent(line.content)}
              </span>
            </div>
          );
        })}
      </div>
    </CodeBlockContainer>
  );
};
